import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Super-admin token analytics (al-tutor migration 108).
 *
 * Reads persisted LangSmith snapshots (per project × agent-type token
 * economy) and triggers async refresh runs. A refresh takes minutes
 * (LangSmith rate limit) — the UI polls `getStats` until the latest run
 * leaves the `running` state.
 */

export interface TokenStatsAgentRow {
  project_name: string;
  agent_type: string;
  runs: number;
  p50_total: number | null;
  mean_total: number | null;
  p99_total: number | null;
  p50_in: number | null;
  p99_in: number | null;
  p50_out: number | null;
  p99_out: number | null;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  mean_cost: number | null;
  p50_cost: number | null;
  p99_cost: number | null;
  total_cost: number;
  gt_20k: number;
  gt_40k: number;
  gt_100k: number;
  gt_500k: number;
}

export interface TokenStatsSnapshotMeta {
  id: string;
  window_days: number;
  status: 'running' | 'done' | 'failed';
  trigger: 'manual' | 'cron';
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface TokenStatsResponse {
  window_days: number;
  snapshot: TokenStatsSnapshotMeta | null;
  latest_run: TokenStatsSnapshotMeta | null;
  refresh_running: boolean;
  rows: TokenStatsAgentRow[];
}

/** Deckungsbeitrag: Credits (Erlös) gegen Modellkosten aus ai_usage_log
 *  (al-tutor migration 186). Plattformzahlen — super_admin only. */
export interface MarginActivity {
  activity: string;
  label: string;
  uses: number;
  revenue_eur: number;
  cost_eur: number;
  margin_pct: number | null;
  cost_per_use_eur: number | null;
  /** Credits one use must cost for 80 % on its OWN cost (overhead separate). */
  credits_for_80: number | null;
}

export interface MarginReport {
  days: number;
  /** Actual start of the comparison — never before the first credit event. */
  window_start: string;
  imported_from: string | null;
  usd_eur: number;
  eur_per_credit: number;
  total: {
    revenue_eur: number;
    cost_eur: number;
    margin_pct: number | null;
    overhead_eur: number;
    overhead_share_pct: number | null;
    unpriced_runs: number;
  };
  activities: MarginActivity[];
  clients: { client_id: string; label: string; revenue_eur: number; cost_eur: number; margin_pct: number | null }[];
  overhead: { source: string; cost_eur: number; tokens: number; unbilled_turn: boolean }[];
}

// --- KI-Routing (al-tutor migration 187) -----------------------------------

export type AiClass = 'light' | 'standard' | 'strong' | 'frontier';

export interface AiModel {
  model: string;
  backend: string;
  class: AiClass | null;
  slots: string[];
  /** USD per 1M tokens from LangSmith's price map; null = unknown there. */
  price: { input: number; output: number } | null;
}

export interface AiTaskUsage {
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  unpriced_calls: number;
  models: Record<string, number>;
}

export interface AiTask {
  task: string;
  area: string;
  where: string;
  notes: string;
  runs_on: string;
  default_model: string;
  default_class: AiClass | null;
  requires: AiClass;
  needs: string[];
  activity: string;
  below_class: boolean;
  routable: boolean;
  has_eval: boolean;
  /** A scenario generator exists — test cases can be generated. */
  has_scenarios: boolean;
  eval_cases: number;
  eval_case_counts: { core: number; random: number };
  candidates: string[];
  /** model → newest run id in which it passed. */
  passed_models: Record<string, string>;
  /** model "@legacy" = pinned to the legacy model (`pinned_legacy`). */
  override: {
    model: string;
    reason: string | null;
    eval_run_id: string | null;
    updated_at: string | null;
    pinned_legacy?: boolean;
  } | null;
  usage: AiTaskUsage | null;
  cost_per_call_usd: Record<string, number | null>;
}

export interface AiEvalModelSummary {
  cases: number;
  pass_rate: number;
  mean_score: number;
  errors: number;
  p50_latency_ms: number | null;
  p95_latency_ms?: number | null;
  avg_cost_usd: number | null;
  passed: boolean;
  /** The task's default model — the pairwise judge compares against it. */
  baseline?: boolean;
  judge?: { better: number; same: number; worse: number; worse_share: number };
}

export interface AiEvalRun {
  id: string;
  task: string;
  models: string[];
  repeats: number;
  status: 'running' | 'done' | 'failed';
  threshold: number;
  summary: {
    models: Record<string, AiEvalModelSummary>;
    cheapest_passing: string | null;
    baseline?: string | null;
    stopped?: string;
    /** Mirrored into LangSmith: dataset at.task.<task>, one experiment per model. */
    langsmith?: { dataset: string; experiments: Record<string, { experiment: string }> };
    /** Replay of recorded production calls, judged against production's answer. */
    replay?: boolean;
    matrix?: boolean;
  } | null;
  error: string | null;
  case_set?: string | null;
  budget_usd?: number | null;
  spent_usd?: number | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface AiEvalResult {
  model: string;
  case_id: string;
  repeat: number;
  score: number | null;
  passed: boolean;
  latency_ms: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cost_usd: number | null;
  output: string | null;
  note: string | null;
  error: string | null;
  why: string | null;
  judge?: { verdict: 'better' | 'same' | 'worse' | null; judge: string | null; reason: string } | null;
}

/** A stored test case (generated from a drawn spec, or a golden file entry). */
export interface AiEvalCase {
  id: string;
  suite: 'core' | 'random';
  label: string;
  golden?: boolean;
  version?: number;
  seed?: number;
  spec?: Record<string, unknown>;
  input: Record<string, unknown>;
  expected: Record<string, unknown>;
  generator_model?: string;
  created_at?: string;
}

export interface AiEvalRunDetail extends AiEvalRun {
  results: AiEvalResult[];
}

export type AiRoutingMode = 'legacy' | 'per_task';
export type AiAgentLoopMode = 'legacy' | 'opt_in' | 'on';

/** Global switch (migration 192): legacy = today's system, ignores every switch. */
export interface AiRoutingControl {
  mode: AiRoutingMode;
  agent_loop: AiAgentLoopMode;
  rev: number;
  reason: string | null;
  updated_by: string | null;
  updated_at: string | null;
  log: {
    mode: AiRoutingMode;
    agent_loop: AiAgentLoopMode;
    rev: number;
    reason: string | null;
    changed_by: string | null;
    changed_at: string | null;
  }[];
}

export interface AiRoutingOverview {
  control: AiRoutingControl;
  usage_days: number;
  /** Replays of recorded production calls may run in this environment. */
  prod_sampled_allowed: boolean;
  langsmith: { enabled: boolean; base_url: string };
  classes: Record<AiClass, string>;
  models: AiModel[];
  tasks: AiTask[];
  runs: AiEvalRun[];
}

@Injectable({ providedIn: 'root' })
export class TokenAnalyticsService {
  private readonly base = `${environment.tutorsBackend}/admin/token-stats`;
  private readonly routingBase = `${environment.tutorsBackend}/admin/ai-routing`;

  constructor(private http: HttpClient) {}

  getStats(windowDays: number): Observable<TokenStatsResponse> {
    const params = new HttpParams().set('window_days', windowDays);
    return this.http.get<TokenStatsResponse>(this.base, { params });
  }

  getMargin(days: number): Observable<MarginReport> {
    return this.http.get<MarginReport>(`${this.base}/margin`, {
      params: new HttpParams().set('days', days),
    });
  }

  importMarginCosts(): Observable<{ imported: number }> {
    return this.http.post<{ imported: number }>(`${this.base}/margin/import`, {});
  }

  getAiRouting(): Observable<AiRoutingOverview> {
    return this.http.get<AiRoutingOverview>(this.routingBase);
  }

  setAiRouting(task: string, model: string, reason: string | null): Observable<AiRoutingOverview> {
    return this.http.put<AiRoutingOverview>(`${this.routingBase}/${encodeURIComponent(task)}`, { model, reason });
  }

  resetAiRouting(task: string): Observable<AiRoutingOverview> {
    return this.http.delete<AiRoutingOverview>(`${this.routingBase}/${encodeURIComponent(task)}`);
  }

  setAiRoutingControl(
    change: { mode?: AiRoutingMode; agent_loop?: AiAgentLoopMode },
    reason: string,
  ): Observable<AiRoutingOverview> {
    return this.http.put<AiRoutingOverview>(`${this.routingBase}/control`, { ...change, reason });
  }

  pinAiRoutingLegacy(task: string, reason: string): Observable<AiRoutingOverview> {
    return this.http.put<AiRoutingOverview>(
      `${this.routingBase}/${encodeURIComponent(task)}/pin-legacy`,
      { reason },
    );
  }

  startAiEval(task: string, models: string[], repeats: number, caseSet = 'core', budgetUsd = 1): Observable<{ run_id: string }> {
    return this.http.post<{ run_id: string }>(`${this.routingBase}/evals`, {
      task, models, repeats, case_set: caseSet, budget_usd: budgetUsd,
    });
  }

  getAiEvalCases(task: string): Observable<{ data: AiEvalCase[] }> {
    return this.http.get<{ data: AiEvalCase[] }>(`${this.routingBase}/cases/${encodeURIComponent(task)}`);
  }

  generateAiEvalCases(task: string, n: number, suite: 'core' | 'random'): Observable<{ accepted: number }> {
    return this.http.post<{ accepted: number }>(`${this.routingBase}/cases/${encodeURIComponent(task)}/generate`, { n, suite });
  }

  setAiEvalCaseActive(caseId: string, active: boolean): Observable<{ ok: boolean }> {
    return this.http.patch<{ ok: boolean }>(`${this.routingBase}/cases/item/${caseId}`, { active });
  }

  getAiEval(runId: string): Observable<AiEvalRunDetail> {
    return this.http.get<AiEvalRunDetail>(`${this.routingBase}/evals/${runId}`);
  }

  refresh(windowDays: number): Observable<{ snapshot_id: string; window_days: number }> {
    return this.http.post<{ snapshot_id: string; window_days: number }>(
      `${this.base}/refresh`,
      { window_days: windowDays },
    );
  }
}
